<i18n
  src="@/locales/views/organization/certifications/basic_certifications_producers/show_producer.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/crop_field_certification.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('list_producers')"
    :params="{
      organization_id: organization_id,
    }"
  ></go-back-link>
  <bread-crumbs :labels="[module_name, producer?.name]"></bread-crumbs>
  <producer-info v-if="load_data_general" :producer="producer"></producer-info>
  <template v-if="load_data_general">
    <div class="flex flex-row justify-end mt-2">
      <div class="panel-plain-label">
        <badge-default
          :label="
            crop_field_certifications.length +
            ' ' +
            $t('labels.total_number_crop_field_certifications')
          "
        />
      </div>
      <div class="grow"></div>
      <new-button
        @click.prevent="goToNewCropFieldCertifications()"
        :label="$t('labels.new_crop_field_certifications_button')"
      />
    </div>
    <div class="mt-8 flex flex-col">
      <alert-danger :errors_data="std_err" />
      <div class="table-first-container">
        <div class="table-second-container">
          <div class="table-third-container">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.crop_field_certification.certification_id") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.crop_field_certification.crop_field_id") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.crop_field_certification.renewal_date") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.crop_field_certification.expiration_date") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.crop_field_certification.active") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.crop_field_certification.suspended") }}
                  </th>
                  <th scope="col" class="table-header-cell"></th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  class="table-row"
                  v-for="crop_field_certification in crop_field_certifications"
                  :key="crop_field_certification.id"
                >
                  <td class="table-cell">
                    {{ crop_field_certification.certification.name }}
                  </td>
                  <td class="table-cell">
                    {{ cropFieldLabel(crop_field_certification.crop_field) }}
                  </td>
                  <td class="table-cell">
                    {{ dateFormatter(crop_field_certification.renewal_date) }}
                  </td>
                  <td class="table-cell">
                    {{
                      dateFormatter(crop_field_certification.expiration_date)
                    }}
                  </td>
                  <td class="table-cell">
                    <yes-no-label :value="crop_field_certification.active" />
                  </td>
                  <td class="table-cell group flex">
                    <yes-no-label
                      :value="crop_field_certification.suspended"
                      :title="
                        crop_field_certification.suspended
                          ? dateFormatter(
                              crop_field_certification.suspended_until
                            )
                          : null
                      "
                    />
                  </td>
                  <td class="table-cell-action">
                    <destroy-button
                      v-if="!crop_field_certification.proc_button_destroy"
                      :disabled_button="false"
                      @destroyEvent="
                        destroyOrganizationCertification(
                          crop_field_certification
                        )
                      "
                      :label="$t('labels.destroy_button_label')"
                      :title="
                        $t('labels.destroy_button_title', {
                          certification_name:
                            crop_field_certification.certification.name,
                        })
                      "
                      :message="$t('labels.destroy_button_message')"
                      :label_confirm="$t('labels.destroy_button_label_confirm')"
                    />
                    <destroy-button-processing
                      v-else
                      :label="$t('labels.destroy_button_label')"
                    />
                    <span class="space-actions"></span>
                    <edit-button
                      @click.prevent="
                        goToEditCropFieldCertification(
                          crop_field_certification.id
                        )
                      "
                      :label="$t('labels.edit_button')"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<script>
import Requester from "@/views/organization/requesters/certifications/basic_certifications_producers/manage_crop_field_certification.js";
import ProducerInfo from "@/components/views/organization/certifications/basic_certifications_producers/ProducerInfo.vue";

export default {
  name: "ShowProducer",
  data() {
    return {
      load_data_general: false,
      proc_button_destroy: false,
      producer: {},
      crop_field_certifications: [],
    };
  },
  components: {
    ProducerInfo,
  },
  props: {
    organization_id: { required: true },
    producer_id: { required: true },
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id,
        },
      }).then(() => this.loadData())
    );
  },
  methods: {
    cropFieldLabel: function (crop_field) {
      let labels = [];
      labels.push(crop_field.name);
      labels.push(crop_field.organization_crop_field.crop_field_code);
      labels = labels.filter((label) => label);
      return labels.join(" - ");
    },
    async loadData() {
      await this.fetchProducer();
      await this.fetchCropFieldCertifications();
      this.load_data_general = true;
    },
    async fetchProducer() {
      let response = await Requester.fetchProducer(
        this.organization_id,
        this.producer_id
      );

      this.producer = response.data;
    },
    async fetchCropFieldCertifications() {
      let response = await Requester.fetchCropFieldCertifications(
        this.organization_id,
        this.producer_id
      );

      this.crop_field_certifications = response.data;
      this.crop_field_certifications.forEach((org_certif) => {
        org_certif["proc_button_destroy"] = false;
      });
    },
    async destroyOrganizationCertification(crop_field_certification) {
      crop_field_certification.proc_button_destroy = true;
      try {
        let response = await Requester.destroy(
          this.organization_id,
          this.producer_id,
          crop_field_certification.id
        );
        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.crop_field_certifications.splice(
          this.crop_field_certifications.indexOf(crop_field_certification),
          1
        );
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
          });
        }
      }
      crop_field_certification.proc_button_destroy = false;
    },
    goToNewCropFieldCertifications() {
      this.$router.push({
        name: this.routeName("new_crop_field_certification"),
        params: {
          organization_id: this.organization_id,
          producer_id: this.producer_id,
        },
      });
    },
    goToEditCropFieldCertification(crop_field_certification_id) {
      this.$router.push({
        name: this.routeName("edit_crop_field_certification"),
        params: {
          organization_id: this.organization_id,
          producer_id: this.producer_id,
          crop_field_certification_id: crop_field_certification_id,
        },
      });
    },
  },
};
</script>
